import { GuardedType, TypeGuard } from "generic-type-guard";
import React from "react";
import { useDispatch } from "react-redux";

import {
	Notification,
	NotificationActions,
	NotificationViews
} from "@com.mgmtp.a12.client/client-core/lib/core/notification";
import { LocalizerContext } from "@com.mgmtp.a12.utils/utils-localization-react/lib/main";
import { Toast, ToastProps } from "@com.mgmtp.a12.widgets/widgets-core/lib/toast";
import { Button } from "@com.mgmtp.a12.widgets/widgets-core/lib/button";
import { Icon } from "@com.mgmtp.a12.widgets/widgets-core/lib/icon";
import { BulletList } from "@com.mgmtp.a12.widgets/widgets-core/lib/bullet-list";

import { RESOURCE_KEYS } from "../localization";
import { isNotificationWithButton, isNotificationWithList } from "../types/notification";

export const CustomNotificationRenderer: NotificationViews.Frame.PropsType["notificationRenderer"] =
	function CustomNotificationRenderer() {
		return NotificationRouter;
	};

type NotificationComponent = ReturnType<NonNullable<NotificationViews.Frame.PropsType["notificationRenderer"]>>;

const NotificationRouter: NotificationComponent = function NotificationRouter({ notification }) {
	if (isNotificationWithButton(notification)) {
		return <ToastWithButton notification={notification} />;
	} else if (isNotificationWithList(notification)) {
		return <ToastWithList notification={notification}></ToastWithList>;
	} else {
		return <FallbackToast notification={notification} />;
	}
};

interface NotificationToastProps<T extends TypeGuard<unknown>> {
	readonly notification: GuardedType<T> & Notification;
}

function useNotificationAsProps(notification: Notification): CustomToastProps {
	const { localizer } = React.useContext(LocalizerContext);

	return {
		notificationId: notification.id,
		variant: notification.severity,
		header: notification.title && localizer(notification.title),
		message: notification.message && localizer(notification.message),
		icon: notification.icon ? <Icon>{notification.icon}</Icon> : undefined
	};
}

const ToastWithButton: React.ComponentType<NotificationToastProps<typeof isNotificationWithButton>> =
	function ToastWithButton({ notification }) {
		const dispatch = useDispatch();
		const { localizer } = React.useContext(LocalizerContext);
		const {
			id,
			button: { label, action, destructive }
		} = notification;

		const customToastProps = useNotificationAsProps(notification);

		const onClick = React.useCallback(() => {
			dispatch(NotificationActions.remove([id]));
			dispatch(action);
		}, [dispatch, id, action]);

		return (
			<CustomToast
				{...customToastProps}
				footer={<Button label={localizer(label)} onClick={onClick} destructive={destructive} />}
			/>
		);
	};

const ToastWithList: React.ComponentType<NotificationToastProps<typeof isNotificationWithList>> =
	function ToastWithList({ notification }) {
		const customToastProps = useNotificationAsProps(notification);
		return (
			<CustomToast
				{...customToastProps}
				message={
					<>
						{customToastProps.message}
						<BulletList.Unordered>
							{notification.items.map(item => (
								<BulletList.Item key={item}>{item}</BulletList.Item>
							))}
						</BulletList.Unordered>
					</>
				}
			/>
		);
	};

const FallbackToast: React.ComponentType<{
	readonly notification: Notification;
}> = function FallbackToast({ notification }) {
	const customToastProps = useNotificationAsProps(notification);
	return <CustomToast {...customToastProps} />;
};

interface CustomToastProps extends Omit<ToastProps, "type" | "onClose" | "duration"> {
	readonly notificationId: string;
}

const CustomToast: React.ComponentType<CustomToastProps> = function CustomToast(props) {
	const dispatch = useDispatch();
	const { localizer } = React.useContext(LocalizerContext);

	const variant = props.variant ?? ("info" as const);
	const header = props.header ?? localizer({ key: RESOURCE_KEYS.component.toast.header[variant] });

	const onClose = React.useCallback(
		() => dispatch(NotificationActions.remove([props.notificationId])),
		[dispatch, props.notificationId]
	);

	return <Toast {...props} type="permanent" variant={variant} header={header} onClose={onClose} />;
};
